import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, Plus } from "lucide-react";

import { OnboardingAfterTrail } from "@/components/onboarding/OnboardingAfterTrail";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";

export interface NoTenantWelcomeStepProps {
  onExistingCompanySelected: () => void;
}

/**
 * Paso 01 — Empresa. The first step of base onboarding when the user has
 * no tenant yet. Asks whether they're connecting an existing company or
 * creating a new one (creation is gated as "Próximamente" until the
 * incorporation flow ships).
 */
export function NoTenantWelcomeStep({
  onExistingCompanySelected,
}: NoTenantWelcomeStepProps): JSX.Element {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<"existing" | null>("existing");

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={1}
            label={t("onboarding.step1.tagLabel", "Empresa")}
          />
          <h1 className="fx-onb-h1">
            {t("onboarding.step1.titleLead", "Empecemos con tu")}{" "}
            <span className="ital">
              {t("onboarding.step1.titleAccent", "empresa.")}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.step1.lead",
              "Vas a configurar Fiscalo en 4 pasos. Decinos primero si tu empresa ya existe o si querés crearla desde cero.",
            )}
          </p>

          <div className="fx-onb-choice-grid">
            <OnboardingChoiceCard
              icon={<Building2 aria-hidden />}
              name={
                <>
                  {t("onboarding.step1.existingNameLead", "Ya tengo una")}{" "}
                  <span className="ital">
                    {t("onboarding.step1.existingNameAccent", "empresa.")}
                  </span>
                </>
              }
              desc={t(
                "onboarding.step1.existingDesc",
                "Continuamos con tu RUT y traemos los datos desde DGI.",
              )}
              meta={t("onboarding.step1.existingMeta", "→ Lo más común")}
              selected={selected === "existing"}
              onClick={() => setSelected("existing")}
            />

            <OnboardingChoiceCard
              icon={<Plus aria-hidden />}
              name={
                <>
                  {t("onboarding.step1.newNameLead", "Quiero")}{" "}
                  <span className="ital">
                    {t("onboarding.step1.newNameAccent", "crearla.")}
                  </span>
                </>
              }
              desc={t(
                "onboarding.step1.newDesc",
                "Constituí tu empresa desde Fiscalo: SAS o unipersonal.",
              )}
              badge={t(
                "onboarding.step1.newBadge",
                "Próximamente",
              )}
              disabled
            />
          </div>

          <div className="fx-onb-actions">
            <span className="side" />
            <span className="side">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-primary"
                disabled={selected !== "existing"}
                onClick={onExistingCompanySelected}
              >
                {t("onboarding.continue", "Continuar")}
                <ArrowRight aria-hidden />
              </button>
            </span>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "onboarding.step1.brandEyebrow",
            "Tu cuenta en 4 pasos · ~ 3 min",
          )}
          quote={
            <>
              {t(
                "onboarding.step1.brandQuoteLead",
                "Abrís tu cuenta en minutos.",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.step1.brandQuoteAccent",
                  "Después configurás Fiscalo — vos",
                )}
              </span>{" "}
              {t(
                "onboarding.step1.brandQuoteTail",
                "o tu contador.",
              )}
            </>
          }
          currentStep={1}
          afterTrail={<OnboardingAfterTrail />}
        />
      }
    />
  );
}
